import * as animationFrame from 'dom-helpers/animationFrame';
import position from 'dom-helpers/position';
import React, { Component, createRef } from 'react';
import clsx from 'clsx';
import DateHeader from 'react-big-calendar/lib/DateHeader';
import Header from 'react-big-calendar/lib/Header';
import PopOverlay from 'react-big-calendar/lib/PopOverlay';
import { navigate, views } from 'react-big-calendar/lib/utils/constants';
import {
  inRange,
  sortWeekEvents,
} from 'react-big-calendar/lib/utils/eventLevels';
import { notify } from 'react-big-calendar/lib/utils/helpers';
import GapAwareDateContentRow from './GapAwareDateContentRow';

function chunkWeeks(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function eventsForWeek(evts, start, end, accessors, localizer) {
  return evts.filter((e) => inRange(e, start, end, accessors, localizer));
}

export default class GapAwareMonthView extends Component {
  static getDerivedStateFromProps({ date, localizer }, state) {
    return {
      date,
      needLimitMeasure: localizer.neq(date, state.date, 'month'),
    };
  }

  static range(date, { localizer }) {
    const start = localizer.firstVisibleDay(date, localizer);
    const end = localizer.lastVisibleDay(date, localizer);
    return { start, end };
  }

  static navigate(date, action, { localizer }) {
    switch (action) {
      case navigate.PREVIOUS:
        return localizer.add(date, -1, 'month');
      case navigate.NEXT:
        return localizer.add(date, 1, 'month');
      default:
        return date;
    }
  }

  static title(date, { localizer }) {
    return localizer.format(date, 'monthHeaderFormat');
  }

  constructor(props) {
    super(props);
    this.state = {
      rowLimit: 5,
      needLimitMeasure: true,
      date: null,
    };
    this.containerRef = createRef();
    this.slotRowRef = createRef();
    this._pendingSelection = [];

    this.getContainer = () => this.containerRef.current;

    this.renderWeek = (week, weekIdx) => {
      const {
        events,
        components,
        selectable,
        getNow,
        selected,
        date,
        localizer,
        longPressThreshold,
        accessors,
        getters,
        showAllEvents,
      } = this.props;
      const { needLimitMeasure, rowLimit } = this.state;
      const weeksEvents = eventsForWeek(
        [...events],
        week[0],
        week[week.length - 1],
        accessors,
        localizer
      );
      const sorted = sortWeekEvents(weeksEvents, accessors, localizer);
      return (
        <GapAwareDateContentRow
          key={weekIdx}
          ref={weekIdx === 0 ? this.slotRowRef : undefined}
          container={this.getContainer}
          className="rbc-month-row"
          getNow={getNow}
          date={date}
          range={week}
          events={sorted}
          maxRows={showAllEvents ? Infinity : rowLimit}
          selected={selected}
          selectable={selectable}
          components={components}
          accessors={accessors}
          getters={getters}
          localizer={localizer}
          renderHeader={this.readerDateHeading}
          renderForMeasure={needLimitMeasure}
          onShowMore={this.handleShowMore}
          onSelect={this.handleSelectEvent}
          onDoubleClick={this.handleDoubleClickEvent}
          onKeyPress={this.handleKeyPressEvent}
          onSelectSlot={this.handleSelectSlot}
          longPressThreshold={longPressThreshold}
          rtl={this.props.rtl}
          resizable={this.props.resizable}
          showAllEvents={showAllEvents}
        />
      );
    };

    this.readerDateHeading = ({
      date: cellDate,
      className,
      key: headingKey,
      ...rest
    }) => {
      const { date: currentDate, getDrilldownView, localizer } = this.props;
      const isOffRange = localizer.neq(currentDate, cellDate, 'month');
      const isCurrent = localizer.isSameDate(cellDate, currentDate);
      const drilldownView = getDrilldownView(cellDate);
      const label = localizer.format(cellDate, 'dateFormat');
      const DateHeaderComponent =
        this.props.components.dateHeader || DateHeader;
      return (
        <div
          key={headingKey}
          {...rest}
          className={clsx(
            className,
            isOffRange && 'rbc-off-range',
            isCurrent && 'rbc-current'
          )}
          role="cell"
        >
          <DateHeaderComponent
            label={label}
            date={cellDate}
            drilldownView={drilldownView}
            isOffRange={isOffRange}
            onDrillDown={(e) =>
              this.handleHeadingClick(cellDate, drilldownView, e)
            }
          />
        </div>
      );
    };

    this.handleSelectSlot = (range, slotInfo) => {
      this._pendingSelection = this._pendingSelection.concat(range);
      clearTimeout(this._selectTimer);
      this._selectTimer = setTimeout(() => this.selectDates(slotInfo));
    };

    this.handleHeadingClick = (cellDate, view, e) => {
      e.preventDefault();
      this.clearSelection();
      notify(this.props.onDrillDown, [cellDate, view]);
    };

    this.handleSelectEvent = (...args) => {
      this.clearSelection();
      notify(this.props.onSelectEvent, args);
    };

    this.handleDoubleClickEvent = (...args) => {
      this.clearSelection();
      notify(this.props.onDoubleClickEvent, args);
    };

    this.handleKeyPressEvent = (...args) => {
      this.clearSelection();
      notify(this.props.onKeyPressEvent, args);
    };

    this.handleShowMore = (evts, cellDate, cell, slot, target) => {
      const {
        popup,
        onDrillDown,
        onShowMore,
        getDrilldownView,
        doShowMoreDrillDown,
      } = this.props;
      this.clearSelection();
      if (popup) {
        const pos = position(cell, this.containerRef.current);
        this.setState({
          overlay: {
            date: cellDate,
            events: evts,
            position: pos,
            target,
          },
        });
      } else if (doShowMoreDrillDown) {
        notify(onDrillDown, [cellDate, getDrilldownView(cellDate) || views.DAY]);
      }
      notify(onShowMore, [evts, cellDate, slot]);
    };

    this.overlayDisplay = () => {
      this.setState({ overlay: null });
    };
  }

  componentDidMount() {
    if (this.state.needLimitMeasure) this.measureRowLimit();
    let running;
    window.addEventListener(
      'resize',
      (this._resizeListener = () => {
        if (!running) {
          animationFrame.request(() => {
            running = false;
            this.setState({ needLimitMeasure: true });
          });
        }
      }),
      false
    );
  }

  componentDidUpdate() {
    if (this.state.needLimitMeasure) this.measureRowLimit();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._resizeListener, false);
  }

  measureRowLimit() {
    this.setState({
      needLimitMeasure: false,
      rowLimit: this.slotRowRef.current.getRowLimit(),
    });
  }

  selectDates(slotInfo) {
    const slots = this._pendingSelection.slice();
    this._pendingSelection = [];
    slots.sort((a, b) => +a - +b);
    const start = new Date(slots[0]);
    const end = new Date(slots[slots.length - 1]);
    end.setDate(slots[slots.length - 1].getDate() + 1);
    notify(this.props.onSelectSlot, {
      slots,
      start,
      end,
      action: slotInfo.action,
      bounds: slotInfo.bounds,
      box: slotInfo.box,
    });
  }

  clearSelection() {
    clearTimeout(this._selectTimer);
    this._pendingSelection = [];
  }

  renderHeaders(row) {
    const { localizer, components } = this.props;
    const first = row[0];
    const last = row[row.length - 1];
    const HeaderComponent = components.header || Header;
    return localizer.range(first, last, 'day').map((day, idx) => (
      <div key={`header_${idx}`} className="rbc-header">
        <HeaderComponent
          date={day}
          localizer={localizer}
          label={localizer.format(day, 'weekdayFormat')}
        />
      </div>
    ));
  }

  renderOverlay() {
    const overlay = this.state?.overlay ?? {};
    const {
      accessors,
      localizer,
      components,
      getters,
      selected,
      popupOffset,
      handleDragStart,
    } = this.props;
    const onHide = () => this.setState({ overlay: null });
    return (
      <PopOverlay
        overlay={overlay}
        accessors={accessors}
        localizer={localizer}
        components={components}
        getters={getters}
        selected={selected}
        popupOffset={popupOffset}
        ref={this.containerRef}
        handleKeyPressEvent={this.handleKeyPressEvent}
        handleSelectEvent={this.handleSelectEvent}
        handleDoubleClickEvent={this.handleDoubleClickEvent}
        handleDragStart={handleDragStart}
        show={!!overlay.position}
        overlayDisplay={this.overlayDisplay}
        onHide={onHide}
      />
    );
  }

  render() {
    const { date, localizer, className } = this.props;
    const month = localizer.visibleDays(date, localizer);
    const weeks = chunkWeeks(month, 7);
    this._weekCount = weeks.length;
    return (
      <div
        className={clsx('rbc-month-view', className)}
        role="table"
        aria-label="Month View"
        ref={this.containerRef}
      >
        <div className="rbc-row rbc-month-header" role="row">
          {this.renderHeaders(weeks[0])}
        </div>
        {weeks.map(this.renderWeek)}
        {this.props.popup && this.renderOverlay()}
      </div>
    );
  }
}
